import {
  Store,
  StoreBizHour,
  defaultLocale,
  defaultTZ,
  OrderType,
} from 'models'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { useLazyLoadStoreQuery } from 'apis/StoreApi'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { setStore } from 'store/OrderingSlice'
import moment from 'moment-timezone'
import {
  calculateAcceptingOrder,
  currentTzTime,
  formatCurrencyWithLocale,
  formatTime as formatT,
  getNextOpeningTime,
  isStoreOpening,
  parseTzTime,
} from 'services'
import { isEmpty } from 'lodash'
import { LoadStoreRequest } from 'models/LoadStoreRequest'

export const useLoadStore = (): {
  loadStore: (params: LoadStoreRequest) => void
  store: Store | undefined
  isLoading: boolean
  isError: boolean
  error: any | undefined
} => {
  const dispatch = useDispatch()
  const currentStore = useSelector<RootState>(
    (state) => state.ordering.store,
  ) as Store
  const [loadStore, { isLoading, isError, error, data: store }] =
    useLazyLoadStoreQuery()

  useEffect(() => {
    if (store) {
      // This is where we can override store data
      // or perform any data correction needed.
      const updatedStore = {
        ...store,
        categories:
          store.categories?.filter((category) => !isEmpty(category.items)) ??
          [],
      }
      dispatch(setStore(updatedStore))
    }
  }, [store, dispatch])

  return {
    loadStore,
    store: currentStore,
    isLoading,
    isError,
    error,
  }
}

const calculateOpeningState = (
  store: Store | undefined,
): {
  isOpening: boolean
  bizHour?: StoreBizHour
  nextOpeningTime?: moment.Moment
} => {
  if (!store) return { isOpening: false }
  const currentDate = currentTzTime(store.timezone)
  const [isOpening, bizHour] = isStoreOpening(store, currentDate)
  const nextOpeningTime = getNextOpeningTime(currentDate, isOpening, bizHour)
  return { isOpening, bizHour, nextOpeningTime }
}

export const useStoreBizHours = (
  storeParam?: Store,
): {
  isOpening: boolean
  bizHour?: StoreBizHour
  nextOpeningTime?: moment.Moment
} => {
  const storeFromRedux = useSelector<RootState>(
    (state) => state.ordering.store,
  ) as Store
  const store = storeParam || storeFromRedux

  const [state, setState] = useState(calculateOpeningState(store))

  useEffect(() => {
    setState(calculateOpeningState(store))
    const interval = setInterval(() => {
      setState(calculateOpeningState(store))
    }, 1000 * 60)
    return () => clearInterval(interval)
  }, [store])

  return state
}

const useStoreAcceptingOrder = (
  store: Store | undefined,
): {
  isAcceptingOrder: boolean
  pauseOrderBefore?: moment.Moment
} => {
  const [state, setState] = useState(calculateAcceptingOrder(store))

  useEffect(() => {
    setState(calculateAcceptingOrder(store))
    const interval = setInterval(() => {
      setState(calculateAcceptingOrder(store))
    }, 1000 * 60)
    return () => clearInterval(interval)
  }, [store])

  return state
}

export const useCurrentStore = () => {
  const store = useSelector<RootState>((state) => state.ordering.store) as Store
  const { locale, timezone } = useMemo(
    () => ({
      locale: store?.locale ?? defaultLocale,
      timezone: store?.timezone ?? defaultTZ,
    }),
    [store],
  )

  const formatCurrency = useCallback(
    (amount: number): string => formatCurrencyWithLocale(amount, locale),
    [locale],
  )

  const currentTime = useCallback(() => {
    return currentTzTime(timezone)
  }, [timezone])

  const formatTime = useCallback(
    (isoTimeStr: string, format?: string): string => {
      return formatT(isoTimeStr, timezone, format)
    },
    [timezone],
  )

  const parseTime = useCallback(
    (time: string, format?: string): moment.Moment => {
      return parseTzTime(time, format, timezone)
    },
    [timezone],
  )

  const { isAcceptingOrder, pauseOrderBefore } = useStoreAcceptingOrder(store)
  const { isOpening } = useStoreBizHours(store)

  const isDeliveryByOwner = store?.delivery_settings?.delivery_type === 'owner'

  const isOrderTypeAllowedWhenClosed = useCallback(
    (orderType: OrderType): boolean => {
      // When store is closed:
      // 1. Pickup and dine-in are always allowed (for later orders)
      // 2. Delivery is only allowed if it's by owner
      if (orderType === 'pickup' || orderType === 'dinein') {
        return true
      } else if (orderType === 'delivery') {
        return isDeliveryByOwner
      }
      return false
    },
    [isDeliveryByOwner],
  )

  const isAsapAllowedForOrderType = useCallback(
    (orderType?: OrderType): boolean => {
      // ASAP is never allowed when store is closed
      if (!isOpening) {
        return false
      }

      if (orderType) {
        return true
      }

      return isOpening
    },
    [isOpening],
  )

  const getAvailableOrderTypes = useCallback((): {
    value: OrderType
    label: string
    disabled: boolean
    hide: boolean
  }[] => {
    const orderTypes = [
      {
        value: 'delivery' as OrderType,
        label: 'Delivery',
        disabled: !isOpening && !isDeliveryByOwner,
        hide: store?.accept_delivery === false,
      },
      {
        value: 'pickup' as OrderType,
        label: 'Pickup',
        disabled: false,
        hide: store?.accept_pickup === false,
      },
      {
        value: 'dinein' as OrderType,
        label: 'Dine-in',
        disabled: false,
        hide: store?.accept_dinein === false,
      },
    ]

    return orderTypes
  }, [isOpening, isDeliveryByOwner, store])

  return {
    store,
    locale,
    timezone,
    isAcceptingOrder,
    pauseOrderBefore,
    isOpening,
    isDeliveryByOwner,
    isOrderTypeAllowedWhenClosed,
    isAsapAllowedForOrderType,
    getAvailableOrderTypes,
    formatCurrency,
    formatTime,
    currentTime,
    parseTime,
  }
}
